import React, { useCallback, useContext, useEffect, useState } from 'react';
import { DollarCircleOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { Cost } from '../../../models';
import { UserContext } from '../../contexts';
import { CostEditPage } from './cost-edit-page';
import { EntityChangeLog } from '../../reports';

export function CostsPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [costs, setCosts] = useState<Cost[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalCost, setEditModalCost] = useState<Cost>();
  const [canEdit, setCanEdit] = useState(false);
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    setCanEdit(hasPermission(user, 'costs.edit'));
  }, [user]);

  const loadCosts = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`costs`)
      .then((response: any) => {
        const responseCosts: Cost[] = response.data;
        const responseData = responseCosts.map(cost => {
          return {
            key: cost.id,
            ...cost,
          };
        });
        setCosts(responseData);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadCosts();
  }, [loadCosts]);

  const onRefreshClick = () => {
    loadCosts();
  };

  const onCostEditSaved = () => {
    closeEditModal();
    loadCosts();
  };

  const openEditModal = () => {
    setEditModalCost(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByCost = (cost: Cost) => {
    setEditModalCost(cost);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalCost(undefined);
  };

  const deleteCost = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`costs/${id}`)
      .then(() => {
        setLoading(false);
        loadCosts();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteCost = (id: string) => {
    const onOk = () => deleteCost(id);
    Modal.confirm({
      title: 'Maliyet sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Maliyet silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
    if (e.key === 'edit') {
      const cost = costs?.find(m => m.id === id);
      if (cost) {
        openEditModalByCost(cost);
      } else {
        openEditModal();
      }
    } else if (e.key === 'del') {
      confirmDeleteCost(id);
    }
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      {canEdit && (
        <>
          <Menu.Item key="edit">
            <Text>Düzenle</Text>
          </Menu.Item>
          <Menu.Item key="del">
            <Text type="danger">Sil</Text>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const tableColumns: any[] = [
    {
      title: 'En düşük',
      dataIndex: 'lowest',
      key: 'lowest',
    },
    {
      title: 'En yüksek',
      dataIndex: 'highest',
      key: 'highest',
    },
    {
      title: 'Öncelik puanı',
      dataIndex: 'priorityScore',
      key: 'priorityScore',
    },
    {
      title: '',
      key: 'action',
      render: (_: any, cost: Cost) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(cost.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <DollarCircleOutlined /> Maliyetler
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button key="add" icon={<PlusOutlined />} onClick={openEditModal} type="primary">
                Yeni maliyet
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={costs} loading={loading} pagination={false} />
      </Content>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'Cost'} recordId={changeLogModalOpen} />
      </Modal>
      <Modal title="Maliyet" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <CostEditPage cost={editModalCost} onSaved={onCostEditSaved} onCancel={closeEditModal} />
      </Modal>
    </>
  );
}
